// Medical certificates, fitness notes and referral letters (FR-EMR-8).
// The text an employer should not necessarily see (diagnosis, reason, referral
// body) is encrypted; dates and the certificate number stay clear so the
// document can be reprinted, listed and checked for overlaps.
import type { AbstractPowerSyncDatabase } from '@powersync/web';
import type { ClinicCrypto } from '@/lib/crypto/clinic-crypto';
import { newId, nowIso } from '@/lib/db/db';

export type DocKind = 'medical_certificate' | 'fitness' | 'referral' | 'other';

export interface DocCtx { db: AbstractPowerSyncDatabase; crypto: ClinicCrypto; clinicId: string; deviceId: string | null; userId: string | null }

export interface DocBody {
  diagnosis_text?: string | null;   // shown only when include_diagnosis is set
  reason?: string | null;           // "unfit for duty", "fit to resume", free text
  recipient?: string | null;        // "To whom it may concern", employer, other clinic
  body?: string | null;             // extra paragraph, doctor's own words
  notes?: string | null;            // internal, never printed
}

export interface NewDocument extends DocBody {
  patient_id: string;
  encounter_id?: string | null;
  doctor_id?: string | null;
  kind: DocKind;
  rest_from?: string | null;
  rest_to?: string | null;
  include_diagnosis?: boolean;
  language?: 'my' | 'en' | 'both';
}

export interface DocumentRow extends DocBody {
  id: string; doc_no: string | null; kind: DocKind; patient_id: string; encounter_id: string | null; doctor_id: string | null;
  rest_from: string | null; rest_to: string | null; rest_days: number | null; include_diagnosis: boolean;
  language: 'my' | 'en' | 'both'; issued_at: string; status: string; print_count: number; cancel_reason: string | null;
}

interface DocStored {
  id: string; doc_no: string | null; kind: DocKind; patient_id: string; encounter_id: string | null; doctor_id: string | null;
  enc: string | null; rest_from: string | null; rest_to: string | null; rest_days: number | null; include_diagnosis: number;
  language: 'my' | 'en' | 'both'; issued_at: string; status: string; print_count: number; cancel_reason: string | null;
}

/** Inclusive day count, the way a clinic counts rest days. */
export function restDays(from: string | null | undefined, to: string | null | undefined): number | null {
  if (!from || !to) return null;
  const a = new Date(from + 'T00:00:00'), b = new Date(to + 'T00:00:00');
  const days = Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;
  return days > 0 ? days : null;
}

/** Next certificate number from this device's reserved block, or null for the server to assign. */
async function nextDocNo(ctx: DocCtx, prefix: string): Promise<string | null> {
  const row = await ctx.db.getOptional<{ id: string; next_value: number }>(
    'select id, next_value from local_numbers where clinic_id = ? and kind = ? and next_value <= range_end', [ctx.clinicId, 'doc_no']);
  if (!row) return null;
  await ctx.db.execute('update local_numbers set next_value = next_value + 1 where id = ?', [row.id]);
  return `${prefix}-${String(row.next_value).padStart(6, '0')}`;
}

export async function issueDocument(ctx: DocCtx, input: NewDocument, prefix: string): Promise<string> {
  const id = newId(); const ts = nowIso();
  const body: DocBody = {
    diagnosis_text: input.diagnosis_text ?? null, reason: input.reason ?? null,
    recipient: input.recipient ?? null, body: input.body ?? null, notes: input.notes ?? null,
  };
  const enc = await ctx.crypto.encrypt(id, body as Record<string, unknown>);
  const docNo = await nextDocNo(ctx, prefix);
  await ctx.db.execute(
    `insert into documents (id, clinic_id, patient_id, encounter_id, doctor_id, kind, doc_no, enc, key_version, rest_from, rest_to, rest_days, include_diagnosis, language, issued_at, issued_by, status, print_count, created_at, created_by, updated_at, updated_by, device_id)
     values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'issued', 0, ?, ?, ?, ?, ?)`,
    [id, ctx.clinicId, input.patient_id, input.encounter_id ?? null, input.doctor_id ?? null, input.kind, docNo, enc, ctx.crypto.active.version,
     input.rest_from ?? null, input.rest_to ?? null, restDays(input.rest_from, input.rest_to), input.include_diagnosis ? 1 : 0,
     input.language ?? 'my', ts, ctx.userId, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
  return id;
}

async function hydrate(ctx: DocCtx, r: DocStored): Promise<DocumentRow> {
  let body: DocBody = {};
  if (r.enc) { try { body = await ctx.crypto.decrypt<DocBody>(r.id, r.enc); } catch { /* encrypted with a key this device lacks */ } }
  return {
    id: r.id, doc_no: r.doc_no, kind: r.kind, patient_id: r.patient_id, encounter_id: r.encounter_id, doctor_id: r.doctor_id,
    rest_from: r.rest_from, rest_to: r.rest_to, rest_days: r.rest_days, include_diagnosis: r.include_diagnosis === 1,
    language: r.language, issued_at: r.issued_at, status: r.status, print_count: r.print_count, cancel_reason: r.cancel_reason, ...body,
  };
}

export async function getDocument(ctx: DocCtx, id: string): Promise<DocumentRow | null> {
  const r = await ctx.db.getOptional<DocStored>('select * from documents where id = ?', [id]);
  return r ? hydrate(ctx, r) : null;
}

export async function listDocuments(ctx: DocCtx, patientId: string): Promise<DocumentRow[]> {
  const rows = await ctx.db.getAll<DocStored>('select * from documents where patient_id = ? order by issued_at desc limit 50', [patientId]);
  return Promise.all(rows.map((r) => hydrate(ctx, r)));
}

export async function documentsForEncounter(ctx: DocCtx, encounterId: string): Promise<DocumentRow[]> {
  const rows = await ctx.db.getAll<DocStored>('select * from documents where encounter_id = ? order by issued_at desc', [encounterId]);
  return Promise.all(rows.map((r) => hydrate(ctx, r)));
}

export async function countPrint(ctx: DocCtx, id: string): Promise<void> {
  await ctx.db.execute('update documents set print_count = print_count + 1, updated_at = ?, updated_by = ? where id = ?', [nowIso(), ctx.userId, id]);
}

/** Certificates are never deleted; a wrong one is cancelled with a reason and stays on record. */
export async function cancelDocument(ctx: DocCtx, id: string, reason: string): Promise<void> {
  const ts = nowIso();
  await ctx.db.execute(`update documents set status = 'cancelled', cancelled_at = ?, cancelled_by = ?, cancel_reason = ?, updated_at = ?, updated_by = ? where id = ?`,
    [ts, ctx.userId, reason, ts, ctx.userId, id]);
}
